import { CancelToken } from 'axios';
import { TFunction } from 'i18next';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect, useDispatch, useSelector } from 'react-redux';
import { Segment, SegmentType } from '~src/interfaces/segment.interface';

import { Topic } from '~src/interfaces/topic.interface';
import { RichCycle } from '~src/modules/spidergraph/types';
import { CapitalizeFirst } from '~src/modules/okrs/manageObjectives/shared/shared.sc';
import SegmentsSelect
  from '~src/modules/surveys/components/schedules/scheduleItem/templates/sections/customInputs/PeopleChips';
import { StoreI } from '~src/rootReducer';
import { RecommendationsRequest } from '~src/services/api/reporting';
import { defaultBaseApi } from '~src/services/ApiController';
import { fetchSearchSegments } from '~src/services/fetch/fetchSearchSegments.service';
import { translateContent } from '~src/services/translationServices';
import { ButtonSizes } from '~src/shared/buttons/basicButtons/BaseButton.sc';
import PrimaryButtonLoading from '~src/shared/buttons/loadingButtons/PrimaryButtonLoading';
import { OptionItem } from '~src/shared/selectors/DdSearchContent';
import SearchListSelector from '~src/shared/selectors/searchListSelector/SearchListSelector';
import { fetchRecommendationsAction } from '~src/store/spidergraph/actions';
import { MediaTypes } from '~src/styles/media-templates';
import { FilterInput, FilterRow, FilterTitle, HeadlineDescription } from './recommendationsFilterDetails.sc';

export function getRecommendationsRequests(
  selectedCycles: RichCycle[],
  selectedSegments: Segment[],
  selectedTopic: string,
): RecommendationsRequest[] {
  const result: Record<string, RecommendationsRequest> = {};
  const segmentIds: string[] = selectedSegments.map((s) => s.id);

  selectedCycles.forEach((selectedCycle) => {
    result[selectedCycle.survey.id] = result[selectedCycle.survey.id] || {
      cycleIds: [],
      surveySubject: selectedCycle.survey.subject,
      surveyId: selectedCycle.survey.id,
      segmentIds,
      topicId: selectedTopic,
    };
    result[selectedCycle.survey.id].cycleIds.push(selectedCycle.getId());
  });

  return Object.values(result);
}

interface OwnProps {
  onSegmentsChange: (segments: Segment[]) => void;
  onTopicChange: (topic?: Topic) => void;
  selectedSegmentsUuids: string[];
  selectedTopic?: string;
  onCyclesChange: (cycles: RichCycle[]) => void;
  selectedCycles: RichCycle[];
  availableCycles: RichCycle[];
  availableTopics: Topic[];
}

interface StateProps {
  media?: MediaTypes;
}

type Props = OwnProps & StateProps;

function formatDay(isoDateString: string) {
  return isoDateString.split('T').shift();
}

export function displayCycleString(
  t: TFunction,
  cycle: { start_date: string; due_date?: string | null; closed: boolean },
): string {
  const closedDesc = cycle.closed ? t('x.cycle.closed') : t('x.cycle.open');
  return `${formatDay(cycle.start_date)} ${
    cycle.due_date ? ' – ' + formatDay(cycle.due_date) : ''
  } (${closedDesc})`;
}

function richCycleToSearchListSelectorItem(t: TFunction, richCycle: RichCycle): OptionItem {
  return {
    id: richCycle.getId(),
    name: displayCycleString(t, richCycle.cycle),
  };
}

function topicToSearchListSelectorItem(topic: Topic): OptionItem {
  return {
    id: topic.id,
    name: translateContent(topic.name),
  };
}

function selectorItemsToRichCycle(selectorItemId: string, richCycles: RichCycle[]): RichCycle {
  const cycle = richCycles.find((cycle) => cycle.getId() === selectorItemId);
  if (!cycle) {
    throw new Error('logic error: selector must not choose from non-available RichCycle');
  }
  return cycle;
}

const RecommendationsFilterDetails: FC<Props> = (props) => {
  const { t } = useTranslation();

  const noneAsAllOption: OptionItem = { name: t('x.analysis.header.filters.none-as-all'), id: 'NONE' };

  const dispatch = useDispatch();
  const [selectedSegments, setSelectedSegments] = useState<Segment[]>([]);
  const isLoadingRecommendations = useSelector(
    (globalStore: StoreI) => globalStore.spidergraph.isLoadingRecommendations,
  );

  useEffect(() => {
    if (props.selectedSegmentsUuids.length) {
      void defaultBaseApi.getSegments({ uuids: props.selectedSegmentsUuids }).then((segmentsResponse) => {
        const segments: Segment[] = segmentsResponse.data;
        props.onSegmentsChange(segments);
        setSelectedSegments(segments);
      });
    }
  }, [props.selectedSegmentsUuids.length]);

  const fetchCycleSuggestions = () =>  {
    return Promise.resolve(props.availableCycles);
  };

  const fetchTopicSuggestions = () => {
    return Promise.resolve(props.availableTopics);
  };

  const onCyclesChange = (optionItemIds: string[] | OptionItem) => {
    if (!Array.isArray(optionItemIds)) {
      return;
    }
    const selectedCycles = optionItemIds.map((id) => selectorItemsToRichCycle(id, props.availableCycles));
    props.onCyclesChange(selectedCycles);
  };

  const onTopicChange = (optionItem: string[] | OptionItem) => {
    if (Array.isArray(optionItem)) {
      return;
    }
    const topic = props.availableTopics.find((t) => t.id === optionItem.id);
    props.onTopicChange(topic);
  };

  const initialTopic = props.availableTopics.find((t) => t.id === props.selectedTopic);
  const initialTopicOption: OptionItem =
    (initialTopic && topicToSearchListSelectorItem(initialTopic)) || noneAsAllOption;

  const withDeactivatedSegments = (value: string, cancelToken: CancelToken) =>
    fetchSearchSegments(value, cancelToken, [SegmentType.Group, SegmentType.Company], true);

  const fetchRecommendations = () => {
    const requestedCycles = props.selectedCycles.length ? props.selectedCycles : [];

    const requests = getRecommendationsRequests(
      requestedCycles,
      selectedSegments,
      props.selectedTopic ? props.selectedTopic : '',
    );
    dispatch(fetchRecommendationsAction(requests));
  };

  const buttonSize = props.media === 'phone' ? ButtonSizes.Wide : ButtonSizes.Medium;

  return (
    <>
      <FilterRow $disabled={isLoadingRecommendations}>
        <FilterTitle data-translation="x.analysis.header.filters.segment.title">
          {t('x.analysis.header.filters.segment.title')}
        </FilterTitle>
        <FilterInput>
          <SegmentsSelect
            fetchSuggestions={withDeactivatedSegments}
            t={t}
            chipsName="segments"
            selectedPeople={selectedSegments}
            onChangePeople={props.onSegmentsChange}
          />
        </FilterInput>
      </FilterRow>
      <FilterRow $disabled={isLoadingRecommendations}>
        <FilterTitle data-translation="x.analysis.header.filters.cycle.title">
          {t('x.analysis.header.filters.cycle.title')}
        </FilterTitle>
        <FilterInput>
          <SearchListSelector
            fetchItems={fetchCycleSuggestions}
            initialOpt={props.selectedCycles.map((r) => r.getId())}
            loadingSearch={false}
            multiSelect
            noMoreItems
            nothingFoundKey=""
            onSelect={onCyclesChange}
            optionsList={props.availableCycles.map((cycle) => richCycleToSearchListSelectorItem(t, cycle))}
          />
        </FilterInput>
      </FilterRow>
      <FilterRow $disabled={isLoadingRecommendations}>
        <FilterTitle data-translation="x.analysis.header.filters.topic.title">
          {t('x.analysis.header.filters.topic.title')}
        </FilterTitle>
        <FilterInput>
          <SearchListSelector
            fetchItems={fetchTopicSuggestions}
            initialOpt={initialTopicOption}
            loadingSearch={false}
            maxWidth={'initial'}
            noMoreItems
            noSearch
            nothingFoundKey=""
            onSelect={onTopicChange}
            optionsList={[noneAsAllOption].concat(props.availableTopics.map(topicToSearchListSelectorItem))}
          />
        </FilterInput>
      </FilterRow>
      <FilterRow $disabled={isLoadingRecommendations}>
        <HeadlineDescription data-translation="x.ai.header_recommendations">
          {t('x.ai.header_recommendations')}
        </HeadlineDescription>
      </FilterRow>
      <FilterRow $disabled={isLoadingRecommendations}>
        <PrimaryButtonLoading
          type="button"
          onClick={fetchRecommendations}
          data-translation="x.ai.submit_button"
          isLoading={isLoadingRecommendations}
          disabled={isLoadingRecommendations}
          $buttonSize={buttonSize}
        >
          <CapitalizeFirst>{t('x.ai.submit_button')}</CapitalizeFirst>
        </PrimaryButtonLoading>
      </FilterRow>
    </>
  );
};

const mapStateToProps = (state: StoreI): StateProps => ({
  media: state.app.mediaQuery,
});

const RecommendationsFilterDetailsConnect = connect(mapStateToProps, null)(RecommendationsFilterDetails);

export default RecommendationsFilterDetailsConnect;
